import {Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {Product, Variation} from '@sportngin/se-commerce-sdk/models/clare';

/**
 * When the product GET omits `variations` but `default_variation_id` is set, loads
 * GET /finance/products/:id/variations (product controller) so display price can use `price_cents`.
 * If that list is empty (some APIs return `[]` while the variation still exists), optionally loads
 * GET /finance/product_variations/:default_variation_id for a single row.
 * Remove when GET …/:id always includes variation rows (or embedded default) with price.
 */
export function mergeVariationsIfMissing(
  item: Product,
  getProductVariants: (productId: string) => Observable<Variation[]>,
  getProductVariantById?: (variationId: string) => Observable<Variation>
): Observable<Product> {
  let result: Observable<Product> = of(item);
  const hasRows = (item.variations?.length ?? 0) > 0;
  const hasDefaultId = item.default_variation_id !== undefined && item.default_variation_id !== null;
  if (!hasRows && hasDefaultId && item.id !== undefined) {
    result = getProductVariants(String(item.id)).pipe(
      switchMap(variations => {
        if (variations.length > 0) {
          return of({...item, variations});
        }
        if (getProductVariantById) {
          return getProductVariantById(String(item.default_variation_id)).pipe(
            map(v => ({...item, variations: [v]})),
            catchError(() => of(item))
          );
        }
        return of(item);
      }),
      catchError(() => {
        if (getProductVariantById && item.default_variation_id !== undefined) {
          return getProductVariantById(String(item.default_variation_id)).pipe(
            map(v => ({...item, variations: [v]})),
            catchError(() => of(item))
          );
        }
        return of(item);
      })
    );
  }
  return result;
}
